import { useEffect, useMemo, useState } from "react";
import { calculateDaily, calculateFd, calculateGold, calculateRd } from "../lib/calculators.js";
import { formatMoney } from "../lib/site.js";

export default function CalculatorPanel({ type, product, roiTable, t }) {
  const [values, setValues] = useState({
    amount: product?.minimumDeposit || 10000,
    monthly: product?.minimumMonthlyDeposit || 1000,
    daily: product?.minimumDailyDeposit || 100,
    rate: product?.interestRate || 7,
    months: product?.tenureMonths || 12,
    days: product?.tenureDays || 365,
    fdType: "reinvestment",
    grams: product?.minimumGoldGrams || 10,
    purity: 22,
    ltv: product?.loanToValuePercent || 70
  });

  useEffect(() => {
    setValues((current) => ({
      ...current,
      amount: product?.minimumDeposit || current.amount,
      monthly: product?.minimumMonthlyDeposit || current.monthly,
      daily: product?.minimumDailyDeposit || current.daily,
      rate: product?.interestRate || current.rate,
      months: product?.tenureMonths || current.months,
      days: product?.tenureDays || current.days,
      grams: product?.minimumGoldGrams || current.grams,
      ltv: product?.loanToValuePercent || current.ltv
    }));
  }, [product]);

  function update(event) {
    const { name, value } = event.target;
    setValues((current) => ({ ...current, [name]: value }));
  }

  const result = useMemo(() => {
    if (type === "fd") {
      const official = findOfficialMaturity(roiTable, Number(values.amount), Number(values.months));
      if (official) return { interest: official - Number(values.amount), maturity: official };
      return calculateFd({ amount: values.amount, rate: values.rate, months: values.months, type: values.fdType });
    }
    if (type === "rd") {
      const official = findOfficialMaturity(roiTable, Number(values.monthly), Number(values.months));
      if (official) return { totalDeposit: Number(values.monthly) * Number(values.months), interest: official - Number(values.monthly) * Number(values.months), maturity: official };
      return calculateRd({ monthly: values.monthly, rate: values.rate, months: values.months });
    }
    if (type === "daily") return calculateDaily({ daily: values.daily, rate: values.rate, days: values.days });
    return calculateGold({ grams: values.grams, purity: values.purity, ltv: values.ltv });
  }, [type, values]);

  return (
    <section className="premium-card grid gap-5 p-5">
      <h2 className="text-2xl font-black text-royal">{t.sections.calculator}</h2>
      {type === "fd" && (
        <div className="grid gap-3 md:grid-cols-2">
          <Field label="Deposit Amount" name="amount" value={values.amount} onChange={update} />
          <Field label="Interest Rate" name="rate" value={values.rate} onChange={update} />
          <Field label="Tenure Months" name="months" value={values.months} onChange={update} />
          <select className="focus-ring rounded-sur border border-[var(--line)] px-4 py-3" name="fdType" value={values.fdType} onChange={update}>
            <option value="monthly">Monthly Interest</option>
            <option value="quarterly">Quarterly Interest</option>
            <option value="reinvestment">Reinvestment</option>
          </select>
        </div>
      )}
      {type === "rd" && (
        <div className="grid gap-3 md:grid-cols-3">
          <Field label="Monthly Deposit" name="monthly" value={values.monthly} onChange={update} />
          <Field label="Interest Rate" name="rate" value={values.rate} onChange={update} />
          <Field label="Tenure Months" name="months" value={values.months} onChange={update} />
        </div>
      )}
      {type === "daily" && (
        <div className="grid gap-3 md:grid-cols-3">
          <Field label="Daily Deposit" name="daily" value={values.daily} onChange={update} />
          <Field label="Interest Rate" name="rate" value={values.rate} onChange={update} />
          <Field label="Tenure Days" name="days" value={values.days} onChange={update} />
        </div>
      )}
      {type === "gold" && (
        <div className="grid gap-3 md:grid-cols-3">
          <Field label="Gold Weight" name="grams" value={values.grams} onChange={update} />
          <Field label="Gold Purity" name="purity" value={values.purity} onChange={update} />
          <Field label="Loan to Value" name="ltv" value={values.ltv} onChange={update} />
        </div>
      )}
      <div className="grid gap-3 md:grid-cols-3">
        {"totalDeposit" in result && <Result label="Total Deposit" value={formatMoney(result.totalDeposit)} />}
        {"interest" in result && <Result label="Interest Earned" value={formatMoney(result.interest)} />}
        {"maturity" in result && <Result label="Maturity Amount" value={formatMoney(result.maturity)} />}
        {"eligible" in result && <Result label="Estimated Eligible Loan" value={formatMoney(result.eligible)} />}
      </div>
    </section>
  );
}

function findOfficialMaturity(table, amount, months) {
  if (!table) return null;
  const column = table.columns.find((item) => Number.parseInt(item.label, 10) === months);
  const row = table.rows.find((item) => item.amount === amount);
  return column && row ? row[column.key] : null;
}

function Field({ label, name, value, onChange }) {
  return (
    <label className="grid gap-2 text-sm font-bold text-muted">
      {label}
      <input className="focus-ring rounded-sur border border-[var(--line)] px-4 py-3 text-ink" type="number" name={name} value={value} onChange={onChange} />
    </label>
  );
}

function Result({ label, value }) {
  return (
    <div className="rounded-sur bg-ivory p-4">
      <span className="text-sm font-bold text-muted">{label}</span>
      <strong className="mt-1 block text-xl text-maroon">{value}</strong>
    </div>
  );
}
